import { useCallback, useMemo, useState } from 'react';
import { entityCategory, EntityItem, SearchQuery } from '../../types';
import {
  ensureEnumNumber,
  getEnumNamesAndValues,
} from '@texas/utils/helpers/enumHelpers';
import { ChatMessageEntityLinkType } from '@texas/api/endpoints/chat/chatApi';
import { searchCustomers } from './search/customer';
import { searchConcept } from './search/concept';
import { searchProductGroup } from './search/productGroup';
import { searchMaterial } from './search/material';
import { searchQuality } from './search/quality';
import { searchDimensionGroup } from './search/dimensionGroup';
import { searchBranch } from './search/branch';
import { randomIdentifier } from '@texas/utils/helpers/commonHelpers';

const entityTypes = getEnumNamesAndValues(ChatMessageEntityLinkType);

export function useSelectEntity() {
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState<EntityItem[]>([]);

  const groups = useMemo(() => {
    const map: Record<string, { items: EntityItem[]; previousCount: number }> =
      {};
    let previousCount = 0;
    values.forEach((item) => {
      const category = entityCategory[item.type];
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!map[category]) {
        map[category] = { items: [], previousCount };
        previousCount = previousCount + 1;
      }
      map[category].items.push(item);
      previousCount++;
    });

    return map;
  }, [values]);

  const filterTypes = useCallback((query: string) => {
    const result = entityTypes
      .filter((c) => c.name.toLowerCase().startsWith(query.toLowerCase()))
      .map((x) => ({
        name: x.name,
        id: x.value as number,
        identifier: randomIdentifier(),
        type: x.value as ChatMessageEntityLinkType,
      }));

    setValues(result);
  }, []);

  const filterEntities = useCallback(
    async (query: SearchQuery, type: ChatMessageEntityLinkType) => {
      switch (ensureEnumNumber(ChatMessageEntityLinkType, type)) {
        case ChatMessageEntityLinkType.Customer: {
          setLoading(true);
          const result = await searchCustomers(query);
          setLoading(false);
          setValues(result);
          break;
        }
        case ChatMessageEntityLinkType.Concept: {
          setLoading(true);
          const result = await searchConcept(query);
          setLoading(false);
          setValues(result);
          break;
        }
        case ChatMessageEntityLinkType.ProductGroup: {
          setLoading(true);
          const result = await searchProductGroup(query);
          setLoading(false);
          setValues(result);
          break;
        }
        case ChatMessageEntityLinkType.Material: {
          setLoading(true);
          const result = await searchMaterial(query);
          setLoading(false);
          setValues(result);
          break;
        }
        case ChatMessageEntityLinkType.Quality: {
          setLoading(true);
          const result = await searchQuality(query);
          setLoading(false);
          setValues(result);
          break;
        }
        case ChatMessageEntityLinkType.DimensionGroup: {
          setLoading(true);
          const result = await searchDimensionGroup(query);
          setLoading(false);
          setValues(result);
          break;
        }
        case ChatMessageEntityLinkType.Branch: {
          setLoading(true);
          const result = await searchBranch(query);
          setLoading(false);
          setValues(result);
          break;
        }
      }
    },
    [],
  );

  return {
    loading,
    groups,
    totalEntities: values.length,
    filterEntities,
    filterTypes,
  };
}
